import {
  ArrowUpRight,
  Check,
  HeartHandshake,
  MessageCircleHeart,
  UsersRound,
} from "lucide-react-native";
import { Platform, Pressable, ScrollView, StyleSheet, Text, useWindowDimensions, View } from "react-native";
import type { ReactNode } from "react";

import { AppShell } from "../../components/layout";
import { WeHonorLogomark } from "../../components/logomark";
import { WeHonorWordmark } from "../../components/wordmark";
import { useReducedMotion } from "../../hooks/useReducedMotion";
import { radii, spacing } from "../../theme/tokens";

const landing = {
  ink: "#142c24",
  inkDeep: "#0f211b",
  inkSoft: "#345047",
  paper: "#fbf8f1",
  paperWarm: "#f0e6d5",
  line: "#d9cebc",
  signal: "#d5b166",
  signalSoft: "#ead6a8",
  quiet: "#6f7f74",
} as const;

export default function LandingPage() {
  const { width } = useWindowDimensions();
  const { reduceMotion } = useReducedMotion();
  const wide = width >= 900;
  const roomy = width >= 720;
  const tight = width < 420;

  const scrollTo = (sectionId: string) => {
    if (Platform.OS !== "web" || typeof document === "undefined") return;

    document.getElementById(sectionId)?.scrollIntoView({
      behavior: reduceMotion ? "auto" : "smooth",
      block: "start",
    });
  };

  return (
    <AppShell
      accessibilityLabel="WeHonor landing page"
      backgroundColor={landing.ink}
      mainContentId="landing-main"
      role="public"
    >
      <ScrollView
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <View nativeID="top" style={styles.page}>
          <View style={styles.hero}>
            <View style={[styles.content, styles.nav, wide ? styles.contentWide : null]}>
              <Pressable
                accessibilityHint="Return to the top of the WeHonor page"
                accessibilityLabel="WeHonor home"
                accessibilityRole="button"
                onPress={() => scrollTo("top")}
                style={({ pressed }) => [styles.brandButton, pressed ? styles.pressed : null]}
              >
                <WeHonorLogomark
                  accentColor={landing.signal}
                  color={landing.paper}
                  size={33}
                />
                <View style={styles.brandCopy}>
                  <WeHonorWordmark color={landing.paper} size="md" />
                </View>
              </Pressable>

              <View style={styles.navActions}>
                {roomy ? (
                  <Pressable
                    accessibilityRole="button"
                    onPress={() => scrollTo("what-we-are-building")}
                    style={({ pressed }) => [styles.navLink, pressed ? styles.pressed : null]}
                  >
                    <Text style={styles.navLinkText}>What we’re building</Text>
                  </Pressable>
                ) : null}
              </View>
            </View>

            <View style={[styles.content, styles.heroBody, wide ? styles.heroBodyWide : null]}>
              <View style={[styles.heroCopy, wide ? styles.heroCopyWide : null]}>
                <Text style={styles.heroKicker}>A quieter future for funeral care</Text>
                <Text accessibilityRole="header" style={[styles.heroTitle, tight ? styles.heroTitleTight : null]}>
                  We’re building the calm layer between a family and the funeral home.
                </Text>
                <Text style={styles.heroDescription}>
                  Families deserve time, clarity, and a place to make decisions together. Funeral professionals deserve a handoff that arrives with the story still attached.
                </Text>
                <View style={styles.heroActions}>
                  <Pressable
                    accessibilityRole="button"
                    onPress={() => scrollTo("what-we-are-building")}
                    style={({ pressed }) => [styles.textButton, pressed ? styles.pressed : null]}
                  >
                    <Text style={styles.textButtonText}>Why it matters</Text>
                  </Pressable>
                </View>
              </View>

              <ProductArtifact compact={!wide} />
            </View>

            <View style={[styles.content, styles.heroFoot, wide ? styles.contentWide : null]}>
              <Text style={styles.heroFootText}>For the people making arrangements.</Text>
              <View style={styles.heroFootRule} />
              <Text style={styles.heroFootText}>For the professionals carrying them through.</Text>
            </View>
          </View>

          <View nativeID="what-we-are-building" style={styles.lightSection}>
            <View style={[styles.content, styles.sectionInner, wide ? styles.contentWide : null]}>
              <View style={[styles.sectionLead, wide ? styles.sectionLeadWide : null]}>
                <Text style={styles.sectionKicker}>The idea</Text>
                <Text accessibilityRole="header" style={styles.sectionTitle}>
                  The hardest work is human. The software should get out of the way.
                </Text>
                <Text style={styles.sectionDescription}>
                  WeHonor is taking shape around a simple belief: the details of a funeral should feel held, not handed over. Our tools connect the family’s pace with the funeral home’s craft.
                </Text>
              </View>

              <View style={styles.principleList}>
                <PrincipleRow
                  description="A family can pause, invite someone to help, and move one meaningful decision at a time."
                  icon={<HeartHandshake color={landing.ink} size={22} strokeWidth={1.9} />}
                  title="Make room for uncertainty."
                />
                <PrincipleRow
                  description="A director can see what has been decided, what is still open, and where a human conversation will help most."
                  icon={<MessageCircleHeart color={landing.ink} size={22} strokeWidth={1.9} />}
                  title="Keep the story in the handoff."
                />
                <PrincipleRow
                  description="A public memorial can hold a person’s life with the same care as the private work around it."
                  icon={<UsersRound color={landing.ink} size={22} strokeWidth={1.9} />}
                  title="Build for the people left behind."
                />
              </View>
            </View>
          </View>

          <View style={styles.handoffSection}>
            <View style={[styles.content, styles.handoffInner, wide ? styles.handoffInnerWide : null]}>
              <View style={styles.handoffCopy}>
                <Text style={styles.handoffKicker}>A better handoff</Text>
                <Text accessibilityRole="header" style={styles.handoffTitle}>
                  Clarity for the family. Context for the director.
                </Text>
                <Text style={styles.handoffDescription}>
                  The work does not end when a form is complete. WeHonor turns the family’s decisions, questions, and notes into a starting point for a more human first call.
                </Text>
                <View style={styles.handoffQuote}>
                  <View style={styles.quoteMark}><Text style={styles.quoteMarkText}>“</Text></View>
                  <Text style={styles.handoffQuoteText}>Ready to review, with room for a conversation.</Text>
                </View>
              </View>
              <HandoffArtifact />
            </View>
          </View>

          <View style={styles.closingSection}>
            <View style={[styles.content, styles.closingInner, wide ? styles.contentWide : null]}>
              <Text accessibilityRole="header" style={styles.closingTitle}>
                We’re making room for people to be people.
              </Text>
              <View style={styles.closingAside}>
                <Text style={styles.closingDescription}>
                  The first version is taking shape now—quietly, carefully, and in close conversation with the people who do this work every day.
                </Text>
              </View>
            </View>
          </View>

          <View style={styles.footer}>
            <View style={[styles.content, styles.footerInner, wide ? styles.contentWide : null]}>
              <View style={styles.footerBrand}>
                <WeHonorLogomark accentColor={landing.signal} color={landing.paper} size={23} />
                <WeHonorWordmark color={landing.paper} size="sm" />
              </View>
              <Text style={styles.footerNote}>More human, by design.</Text>
            </View>
          </View>
        </View>
      </ScrollView>
    </AppShell>
  );
}

function PrincipleRow({
  description,
  icon,
  title,
}: {
  description: string;
  icon: ReactNode;
  title: string;
}) {
  return (
    <View style={styles.principleRow}>
      <View style={styles.principleIcon}>{icon}</View>
      <View style={styles.principleCopy}>
        <Text style={styles.principleTitle}>{title}</Text>
        <Text style={styles.principleDescription}>{description}</Text>
      </View>
      <ArrowUpRight color={landing.quiet} size={18} strokeWidth={1.8} />
    </View>
  );
}

function ProductArtifact({ compact = false }: { compact?: boolean }) {
  return (
    <View
      accessible
      accessibilityLabel="Concept illustration of the WeHonor family arrangement studio"
      style={[styles.artifact, compact ? styles.artifactCompact : null]}
    >
      <View style={styles.artifactWindowBar}>
        <View style={styles.windowDots}>
          <View style={[styles.windowDot, { backgroundColor: "#d89b7f" }]} />
          <View style={[styles.windowDot, { backgroundColor: "#d4b46d" }]} />
          <View style={[styles.windowDot, { backgroundColor: "#9eb49c" }]} />
        </View>
        <Text style={styles.artifactWindowLabel}>wehonor / family space</Text>
        <Text style={styles.artifactWindowStatus}>saved</Text>
      </View>

      <View style={styles.artifactBody}>
        <View style={styles.artifactHeadingRow}>
          <View style={styles.artifactHeadingCopy}>
            <Text style={styles.artifactKicker}>Family arrangement studio</Text>
            <Text style={styles.artifactTitle}>A few things to decide together.</Text>
          </View>
          <View style={styles.savedBadge}>
            <View style={styles.savedDot} />
            <Text style={styles.savedText}>Saved</Text>
          </View>
        </View>

        <View style={styles.artifactPath}>
          <ArtifactStep complete label="Tell us about them" />
          <ArtifactStep active label="Choose what feels right" />
          <ArtifactStep label="Leave a note for the director" />
        </View>

        <View style={styles.artifactInvite}>
          <View style={styles.artifactInviteCopy}>
            <Text style={styles.artifactInviteTitle}>You don’t have to do this alone.</Text>
            <Text style={styles.artifactInviteDescription}>Invite someone you trust to help you decide.</Text>
          </View>
          <View style={styles.inviteIcon}>
            <UsersRound color={landing.ink} size={17} strokeWidth={1.8} />
          </View>
        </View>
      </View>
    </View>
  );
}

function ArtifactStep({ active = false, complete = false, label }: { active?: boolean; complete?: boolean; label: string }) {
  return (
    <View style={styles.artifactStep}>
      <View style={[styles.stepMarker, complete ? styles.stepMarkerComplete : null, active ? styles.stepMarkerActive : null]}>
        {complete ? <Check color={landing.paper} size={14} strokeWidth={2.4} /> : <Text style={[styles.stepNumber, active ? styles.stepNumberActive : null]}>•</Text>}
      </View>
      <Text style={[styles.stepLabel, active ? styles.stepLabelActive : null]}>{label}</Text>
      {complete ? <Text style={styles.stepState}>done</Text> : active ? <Text style={styles.stepStateActive}>next</Text> : null}
    </View>
  );
}

function HandoffArtifact() {
  return (
    <View accessible accessibilityLabel="Concept illustration of a director-ready WeHonor case handoff" style={styles.handoffArtifact}>
      <View style={styles.handoffArtifactTop}>
        <Text style={styles.handoffArtifactKicker}>Director handoff</Text>
        <View style={styles.readyBadge}>
          <Check color={landing.ink} size={13} strokeWidth={2.6} />
          <Text style={styles.readyBadgeText}>ready to review</Text>
        </View>
      </View>
      <Text style={styles.handoffArtifactTitle}>A clear next conversation.</Text>
      <Text style={styles.handoffArtifactDescription}>The family’s choices, questions, and open decisions—together in one place.</Text>
      <View style={styles.handoffRows}>
        <HandoffRow label="Decisions made" value="3" />
        <HandoffRow label="Questions to discuss" value="2" />
        <HandoffRow label="Family members invited" value="4" />
      </View>
      <View style={styles.handoffArtifactFoot}>
        <Text style={styles.handoffArtifactFootText}>Shared with care</Text>
        <ArrowUpRight color={landing.signal} size={16} strokeWidth={2} />
      </View>
    </View>
  );
}

function HandoffRow({ label, value }: { label: string; value: string }) {
  return (
    <View style={styles.handoffRow}>
      <Text style={styles.handoffRowLabel}>{label}</Text>
      <Text style={styles.handoffRowValue}>{value}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  scrollContent: {
    flexGrow: 1,
  },
  page: {
    width: "100%",
    overflow: "hidden",
  },
  content: {
    width: "100%",
    maxWidth: 1240,
    alignSelf: "center",
    paddingHorizontal: 20,
  },
  contentWide: {
    paddingHorizontal: 48,
  },
  hero: {
    backgroundColor: landing.ink,
    paddingBottom: 42,
  },
  nav: {
    minHeight: 82,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: spacing.md,
  },
  brandButton: {
    minHeight: 48,
    flexDirection: "row",
    alignItems: "center",
    gap: 11,
  },
  brandCopy: {
    gap: 1,
  },
  brandName: {
    color: landing.paper,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 15,
    letterSpacing: 2.2,
    lineHeight: 20,
  },
  brandTagline: {
    color: landing.signalSoft,
    fontFamily: "Manrope_500Medium",
    fontSize: 10,
    letterSpacing: 0.2,
    lineHeight: 14,
  },
  navActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.lg,
  },
  navLink: {
    minHeight: 44,
    justifyContent: "center",
    paddingHorizontal: 4,
  },
  navLinkText: {
    color: "rgba(251, 248, 241, 0.72)",
    fontFamily: "Manrope_600SemiBold",
    fontSize: 12,
    lineHeight: 18,
  },
  heroBody: {
    gap: spacing.xxl,
    paddingTop: 60,
  },
  heroBodyWide: {
    flexDirection: "row",
    alignItems: "center",
    gap: 56,
    paddingTop: 84,
    paddingBottom: 76,
  },
  heroCopy: {
    gap: 22,
  },
  heroCopyWide: {
    flex: 1,
    minWidth: 0,
    maxWidth: 625,
  },
  heroKicker: {
    color: landing.signal,
    fontFamily: "Manrope_700Bold",
    fontSize: 11,
    letterSpacing: 1.45,
    lineHeight: 16,
    textTransform: "uppercase",
  },
  heroTitle: {
    color: landing.paper,
    fontFamily: "CormorantGaramond_700Bold",
    fontSize: 58,
    lineHeight: 57,
    letterSpacing: -0.6,
    maxWidth: 650,
  },
  heroTitleTight: {
    fontSize: 52,
    lineHeight: 51,
  },
  heroDescription: {
    color: "rgba(251, 248, 241, 0.78)",
    fontFamily: "Manrope_400Regular",
    fontSize: 16,
    lineHeight: 26,
    maxWidth: 560,
  },
  heroActions: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 14,
    paddingTop: 4,
  },
  textButton: {
    minHeight: 44,
    justifyContent: "center",
    paddingHorizontal: 4,
  },
  textButtonText: {
    color: landing.paper,
    fontFamily: "Manrope_700Bold",
    fontSize: 13,
    lineHeight: 18,
    textDecorationLine: "underline",
    textDecorationColor: "rgba(251, 248, 241, 0.42)",
  },
  pressed: {
    opacity: 0.66,
  },
  heroFoot: {
    flexDirection: "row",
    alignItems: "center",
    gap: 14,
    paddingTop: 28,
  },
  heroFootText: {
    color: "rgba(251, 248, 241, 0.52)",
    flexShrink: 1,
    fontFamily: "Manrope_500Medium",
    fontSize: 11,
    lineHeight: 16,
  },
  heroFootRule: {
    width: 32,
    height: 1,
    backgroundColor: "rgba(234, 214, 168, 0.38)",
  },
  artifact: {
    width: "100%",
    maxWidth: 490,
    alignSelf: "center",
    overflow: "hidden",
    borderRadius: 18,
    backgroundColor: landing.paper,
    shadowColor: "#08130f",
    shadowOpacity: 0.26,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 8 },
    elevation: 8,
  },
  artifactCompact: {
    maxWidth: 620,
  },
  artifactWindowBar: {
    minHeight: 38,
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    paddingHorizontal: 15,
    backgroundColor: "#e9e1d2",
  },
  windowDots: {
    flexDirection: "row",
    gap: 5,
  },
  windowDot: {
    width: 7,
    height: 7,
    borderRadius: 4,
  },
  artifactWindowLabel: {
    color: landing.inkSoft,
    flex: 1,
    fontFamily: "Manrope_700Bold",
    fontSize: 9,
    letterSpacing: 0.5,
    lineHeight: 13,
    textTransform: "uppercase",
  },
  artifactWindowStatus: {
    color: landing.quiet,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 10,
    lineHeight: 14,
  },
  artifactBody: {
    gap: 22,
    padding: 24,
  },
  artifactHeadingRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
  },
  artifactHeadingCopy: {
    flex: 1,
    minWidth: 0,
    gap: 6,
  },
  artifactKicker: {
    color: landing.quiet,
    fontFamily: "Manrope_700Bold",
    fontSize: 9,
    letterSpacing: 1.1,
    lineHeight: 13,
    textTransform: "uppercase",
  },
  artifactTitle: {
    color: landing.ink,
    fontFamily: "CormorantGaramond_700Bold",
    fontSize: 28,
    lineHeight: 30,
  },
  savedBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
    paddingTop: 2,
  },
  savedDot: {
    width: 7,
    height: 7,
    borderRadius: 4,
    backgroundColor: "#6a8e6e",
  },
  savedText: {
    color: landing.quiet,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 10,
    lineHeight: 14,
  },
  artifactPath: {
    gap: 0,
    borderTopWidth: 1,
    borderTopColor: landing.line,
  },
  artifactStep: {
    minHeight: 50,
    flexDirection: "row",
    alignItems: "center",
    gap: 11,
    borderBottomWidth: 1,
    borderBottomColor: landing.line,
  },
  stepMarker: {
    width: 22,
    height: 22,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 11,
    borderWidth: 1,
    borderColor: "#a8b2a8",
  },
  stepMarkerComplete: {
    borderColor: landing.ink,
    backgroundColor: landing.ink,
  },
  stepMarkerActive: {
    borderColor: landing.signal,
    backgroundColor: "#f5e8c9",
  },
  stepNumber: {
    color: "#8e9a90",
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 16,
    lineHeight: 18,
  },
  stepNumberActive: {
    color: landing.ink,
  },
  stepLabel: {
    color: landing.quiet,
    flex: 1,
    fontFamily: "Manrope_500Medium",
    fontSize: 12,
    lineHeight: 17,
  },
  stepLabelActive: {
    color: landing.ink,
    fontFamily: "Manrope_700Bold",
  },
  stepState: {
    color: landing.quiet,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 10,
    lineHeight: 14,
  },
  stepStateActive: {
    color: "#987431",
    fontFamily: "Manrope_700Bold",
    fontSize: 10,
    lineHeight: 14,
    textTransform: "uppercase",
  },
  artifactInvite: {
    flexDirection: "row",
    alignItems: "center",
    gap: 13,
    padding: 14,
    borderRadius: 12,
    backgroundColor: "#f1eadf",
  },
  artifactInviteCopy: {
    flex: 1,
    minWidth: 0,
    gap: 3,
  },
  artifactInviteTitle: {
    color: landing.ink,
    fontFamily: "Manrope_700Bold",
    fontSize: 11,
    lineHeight: 16,
  },
  artifactInviteDescription: {
    color: landing.quiet,
    fontFamily: "Manrope_400Regular",
    fontSize: 10,
    lineHeight: 15,
  },
  inviteIcon: {
    width: 34,
    height: 34,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 17,
    backgroundColor: landing.signal,
  },
  lightSection: {
    backgroundColor: landing.paper,
  },
  sectionInner: {
    gap: 58,
    paddingTop: 106,
    paddingBottom: 116,
  },
  sectionLead: {
    gap: 18,
  },
  sectionLeadWide: {
    maxWidth: 760,
  },
  sectionKicker: {
    color: landing.inkSoft,
    fontFamily: "Manrope_700Bold",
    fontSize: 11,
    letterSpacing: 1.3,
    lineHeight: 16,
    textTransform: "uppercase",
  },
  sectionTitle: {
    color: landing.ink,
    fontFamily: "CormorantGaramond_700Bold",
    fontSize: 48,
    lineHeight: 48,
    letterSpacing: -0.4,
    maxWidth: 720,
  },
  sectionDescription: {
    color: landing.inkSoft,
    fontFamily: "Manrope_400Regular",
    fontSize: 16,
    lineHeight: 26,
    maxWidth: 650,
  },
  principleList: {
    borderTopWidth: 1,
    borderTopColor: landing.line,
  },
  principleRow: {
    minHeight: 104,
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
    borderBottomWidth: 1,
    borderBottomColor: landing.line,
  },
  principleIcon: {
    width: 48,
    height: 48,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 24,
    backgroundColor: landing.paperWarm,
  },
  principleCopy: {
    flex: 1,
    minWidth: 0,
    gap: 4,
  },
  principleTitle: {
    color: landing.ink,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 14,
    lineHeight: 20,
  },
  principleDescription: {
    color: landing.inkSoft,
    fontFamily: "Manrope_400Regular",
    fontSize: 14,
    lineHeight: 21,
    maxWidth: 690,
  },
  handoffSection: {
    backgroundColor: landing.inkDeep,
  },
  handoffInner: {
    gap: 56,
    paddingTop: 100,
    paddingBottom: 104,
  },
  handoffInnerWide: {
    flexDirection: "row",
    alignItems: "center",
  },
  handoffCopy: {
    flex: 1,
    minWidth: 0,
    gap: 18,
  },
  handoffKicker: {
    color: landing.signal,
    fontFamily: "Manrope_700Bold",
    fontSize: 11,
    letterSpacing: 1.3,
    lineHeight: 16,
    textTransform: "uppercase",
  },
  handoffTitle: {
    color: landing.paper,
    fontFamily: "CormorantGaramond_700Bold",
    fontSize: 49,
    lineHeight: 49,
    letterSpacing: -0.4,
    maxWidth: 590,
  },
  handoffDescription: {
    color: "rgba(251, 248, 241, 0.74)",
    fontFamily: "Manrope_400Regular",
    fontSize: 16,
    lineHeight: 26,
    maxWidth: 520,
  },
  handoffQuote: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    paddingTop: 10,
  },
  quoteMark: {
    width: 30,
    height: 30,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 15,
    backgroundColor: "rgba(213, 177, 102, 0.17)",
  },
  quoteMarkText: {
    color: landing.signal,
    fontFamily: "CormorantGaramond_700Bold",
    fontSize: 23,
    lineHeight: 28,
  },
  handoffQuoteText: {
    color: landing.signalSoft,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 12,
    lineHeight: 18,
  },
  handoffArtifact: {
    width: "100%",
    maxWidth: 470,
    alignSelf: "center",
    gap: 18,
    padding: 24,
    borderRadius: 16,
    backgroundColor: "#efe5d4",
  },
  handoffArtifactTop: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
  },
  handoffArtifactKicker: {
    color: landing.inkSoft,
    fontFamily: "Manrope_700Bold",
    fontSize: 10,
    letterSpacing: 1.2,
    lineHeight: 14,
    textTransform: "uppercase",
  },
  readyBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
    paddingHorizontal: 8,
    paddingVertical: 5,
    borderRadius: radii.pill,
    backgroundColor: "#d6e0ce",
  },
  readyBadgeText: {
    color: landing.ink,
    fontFamily: "Manrope_700Bold",
    fontSize: 9,
    lineHeight: 13,
    textTransform: "uppercase",
  },
  handoffArtifactTitle: {
    color: landing.ink,
    fontFamily: "CormorantGaramond_700Bold",
    fontSize: 33,
    lineHeight: 34,
  },
  handoffArtifactDescription: {
    color: landing.inkSoft,
    fontFamily: "Manrope_400Regular",
    fontSize: 13,
    lineHeight: 20,
    maxWidth: 360,
  },
  handoffRows: {
    borderTopWidth: 1,
    borderTopColor: "#cfc0a7",
  },
  handoffRow: {
    minHeight: 38,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottomWidth: 1,
    borderBottomColor: "#cfc0a7",
  },
  handoffRowLabel: {
    color: landing.inkSoft,
    fontFamily: "Manrope_500Medium",
    fontSize: 11,
    lineHeight: 16,
  },
  handoffRowValue: {
    color: landing.ink,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 14,
    lineHeight: 18,
  },
  handoffArtifactFoot: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  handoffArtifactFootText: {
    color: landing.inkSoft,
    fontFamily: "Manrope_700Bold",
    fontSize: 11,
    lineHeight: 16,
  },
  closingSection: {
    backgroundColor: landing.signal,
  },
  closingInner: {
    gap: 36,
    paddingTop: 88,
    paddingBottom: 92,
  },
  closingTitle: {
    color: landing.ink,
    fontFamily: "CormorantGaramond_700Bold",
    fontSize: 54,
    lineHeight: 53,
    letterSpacing: -0.5,
    maxWidth: 720,
  },
  closingAside: {
    alignSelf: "flex-end",
    gap: 18,
    maxWidth: 430,
  },
  closingDescription: {
    color: landing.inkSoft,
    fontFamily: "Manrope_500Medium",
    fontSize: 15,
    lineHeight: 24,
  },
  footer: {
    backgroundColor: landing.inkDeep,
  },
  footerInner: {
    minHeight: 86,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: spacing.md,
  },
  footerBrand: {
    flexDirection: "row",
    alignItems: "center",
    gap: 9,
  },
  footerWordmark: {
    color: landing.paper,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 11,
    letterSpacing: 1.7,
    lineHeight: 16,
  },
  footerNote: {
    color: "rgba(251, 248, 241, 0.5)",
    fontFamily: "Manrope_500Medium",
    fontSize: 11,
    lineHeight: 16,
  },
});
